// 心灵捕手, in its own hut on the 游乐船 (zone "mind"; src/mind.ts runs it, office/games/mind.ts; this only shows what
// the office sends): everyone gets the public m_state; the 主持人 also m_host (the question bank while choosing, then
// every answer with its author), each answerer m_mine (their own answer). Nobody else's page ever gets who wrote what
// before the reveal. The office's lines and refusals come as codes (m_msg {code, params}, m_err {reason, params}),
// worded here (mind.msg.*, mind.err.*). Built from the shared full-screen pieces in games.js (passed in), so it looks
// like the other games; the hut's small card (lobby) too.
import { icon, put } from '/icons.js';
import { t, onLang, lang } from '/i18n.js';
import { voiceNote } from '/game-voice.js';
import { autoTour } from '/tours.js';

export const MIND_HUT = 'mind';
const LEVELS = ['easy', 'warm', 'deep'];
const PER_PAGE = 4, MIN = 3, MAX = 10;
const TIPS = ['mind.tip.ready', 'mind.tip.care', 'mind.tip.pairs'];
/** A seat's tag, by what it is this step. */
const TAG = { organizer: 'mind.tag.organizer', joined: 'mind.tag.joined', subject: 'mind.tag.subject', host: 'mind.tag.host', answered: 'mind.tag.answered', away: 'mind.tag.away' };
/** What the phase line says (and its icon), by phase and my part in it. */
const PHASE = {
  'join.in': { icon: 'heart', text: 'mind.phase.join.in' }, 'join.out': { icon: 'heart', text: 'mind.phase.join.out' },
  'ask.host': { icon: 'bubble', text: 'mind.phase.ask.host' }, 'ask.subject': { icon: 'bubble', text: 'mind.phase.ask.subject' }, 'ask.other': { icon: 'bubble', text: 'mind.phase.ask.other' },
  'answer.subject': { icon: 'heart', text: 'mind.phase.answer.subject' }, 'answer.host': { icon: 'bubble', text: 'mind.phase.answer.host' },
  'answer.sent': { icon: 'pencil', text: 'mind.phase.answer.sent' }, 'answer.write': { icon: 'pencil', text: 'mind.phase.answer.write' }, 'answer.watch': { icon: null, text: 'mind.phase.answer.watch' },
  'match.subject': { icon: 'target', text: 'mind.phase.match.subject' }, 'match.other': { icon: 'target', text: 'mind.phase.match.other' },
  'reveal.right': { icon: 'party', text: 'mind.phase.reveal' }, 'reveal.none': { icon: 'heart', text: 'mind.phase.reveal' }, 'reveal.skip': { icon: null, text: 'mind.phase.skip' },
  final: { icon: 'trophy', text: 'mind.phase.final' },
};

export function initMind({ send, myId, shell, lobby, el, btn, addLog, scoreList, leaderboard, fulls, inZone, hutCount, micOn = () => true }) {
  const M = { phase: 'idle', round: 0, endsAt: 0, organizer: '', players: [], pick: null, subject: null, host: null, qid: null, question: '', questionEn: '', level: '', answered: [],
    cards: [], candidates: [], authors: [], guesses: [], right: 0, aborted: '', been: [], scores: [], away: [],
    hostAnswers: [], questions: [], mine: null, open: false, dismissed: false, lv: 'easy', page: 0, guess: [], guessKey: '' };
  const m = shell('mfull', t('mind.title'), t('mind.chat.placeholder'), 'heart');
  m.input.maxLength = 120;
  const lob = lobby('mlobby', t('mind.title'), t('mind.lobby.rules'), 'heart', 'mind');
  const tipsLob = el('div', 'mtips mlobtips'); lob.d.insertBefore(tipsLob, lob.d.querySelector('.glbtns'));
  const stage = el('div', 'sstage mstage'), qbox = el('div', 'mq'), phaseLine = el('div', 'sphase'), voice = el('div', 'gvoice'), body = el('div', 'mbody'), seats = el('div', 'sseats mseats');
  const tips = el('div', 'mtips');
  m.stage.append(stage);
  m.form.onsubmit = (e) => { e.preventDefault(); const v = m.input.value.trim(); if (v) send({ t: 'm_chat', text: v }); m.input.value = ''; };
  const full = {
    isOpen: () => M.open,
    close: () => { M.open = false; M.dismissed = true; render(); },
    focus: () => { if (!m.input.disabled) m.input.focus(); },
  };
  fulls.push(full);
  m.leave.onclick = full.close;
  const act = (a, extra = {}) => send({ t: 'm_act', act: a, ...extra });
  /** A text field of the stage: keys stay in it (no walking, no M for mute). */
  const field = (tag, cls, ph, max) => {
    const f = el(tag, cls); f.placeholder = t(ph); f.dataset.ph = ph; f.maxLength = max; f.autocomplete = 'off';
    for (const ev of ['keydown', 'keyup']) f.addEventListener(ev, (e) => { e.stopPropagation(); if (e.key === 'Escape') f.blur(); });
    return f;
  };
  const name = (id) => M.players.find((p) => p.id === id)?.name ?? M.candidates.find((p) => p.id === id)?.name ?? M.scores.find((p) => p.id === id)?.name ?? '?';
  const running = () => M.phase !== 'idle' && M.phase !== 'final';
  const qText = () => (lang === 'en' ? M.questionEn || M.question : M.question);

  // The answer form (built once, so typing survives updates).
  const aform = el('form', 'mform'), aview = field('input', '', 'mind.answer.view', 80), awhy = field('textarea', '', 'mind.answer.why', 200), asend = el('button', 'gprimary');
  const alabel = el('label', 'slabel'), anote = el('div', 'mnote'), arow = el('div', 'mrow');
  arow.append(asend);
  aform.append(alabel, aview, awhy, arow, anote);
  aform.onsubmit = (e) => { e.preventDefault(); const v = aview.value.trim(); if (!v) { aview.focus(); return; } act('answer', { view: v, why: awhy.value.trim() }); };
  // The 主持人's own question (built once).
  const qform = el('form', 'mrow mown'), qown = field('input', '', 'mind.ask.own', 80), qgo = el('button');
  qform.append(qown, qgo);
  qform.onsubmit = (e) => { e.preventDefault(); const v = qown.value.trim(); if (v) act('ask', { text: v }); };
  // the forms stay in the stage (shown when needed), so an update while typing doesn't take the focus away
  const hostBox = el('div', 'mhost');
  stage.append(qbox, phaseLine, voice, body, aform, qform, hostBox, seats, tips, m.card);

  function chips(list, on, pickFn) {
    const row = el('div', 'mchips');
    for (const [id, label] of list) { const b = el('button', 'mchip' + (id === on ? ' on' : ''), label); b.type = 'button'; b.onclick = () => pickFn(id); row.append(b); }
    return row;
  }
  function card(c, i, extra) {
    const d = el('div', 'mcard'), head = el('div', 'mchead');
    head.append(el('span', 'mletter', String.fromCharCode(65 + i)), el('b', 'mview', c.view));
    d.append(head);
    if (c.why) d.append(el('div', 'mwhy', t('mind.card.why', { why: c.why })));
    if (extra) extra(d);
    return d;
  }
  const tipList = (box) => box.replaceChildren(put(el('b'), 'bulb', t('mind.tips.title')), ...TIPS.map((k) => el('span', '', t(k))));
  /** Which PHASE line is mine now. */
  function phaseKey(me, joined, isHost, isSubject) {
    const ph = M.phase;
    if (ph === 'join') return joined ? 'join.in' : 'join.out';
    if (ph === 'ask') return isHost ? 'ask.host' : isSubject ? 'ask.subject' : 'ask.other';
    if (ph === 'answer') return isSubject ? 'answer.subject' : isHost ? 'answer.host' : !joined ? 'answer.watch' : M.mine ? 'answer.sent' : 'answer.write';
    if (ph === 'match') return isSubject ? 'match.subject' : 'match.other';
    if (ph === 'reveal') return M.aborted ? 'reveal.skip' : M.right ? 'reveal.right' : 'reveal.none';
    if (ph === 'final') return 'final';
    return null;
  }

  function mRender() {
    // a field of the body (a guess's reason) keeps its focus and caret over the rebuild
    const focus = body.contains(document.activeElement) ? document.activeElement : null, sel = focus && [focus.selectionStart, focus.selectionEnd];
    draw();
    if (focus && focus.isConnected && document.activeElement !== focus) { focus.focus({ preventScroll: true }); try { focus.setSelectionRange(...sel); } catch {} }
  }
  function draw() {
    const me = myId(), joined = M.players.some((p) => p.id === me), isHost = me === M.host, isSubject = me === M.subject, org = me === M.organizer;
    const ph = M.phase, answerers = M.players.filter((p) => p.id !== M.subject && p.id !== M.host), done = answerers.filter((p) => M.answered.includes(p.id)).length;
    m.round.textContent = M.round ? t('mind.round', { n: M.round }) : ph === 'join' ? t('mind.signup') : '';
    m.who.textContent = M.subject && running() && ph !== 'join' ? t('mind.who', { subject: name(M.subject), host: name(M.host) }) : '';
    // the question
    qbox.replaceChildren();
    if (M.question && ph !== 'join') qbox.append(el('span', 'slabel', t('mind.q.about', { name: name(M.subject), level: t(`mind.level.${LEVELS.includes(M.level) ? M.level : 'own'}`) })), el('b', '', qText()));
    qbox.style.display = qbox.childNodes.length ? '' : 'none';
    const pk = phaseKey(me, joined, isHost, isSubject), pl = pk && PHASE[pk];
    put(phaseLine, pl?.icon ?? null, pl ? t(pl.text, { subject: name(M.subject), host: name(M.host), done, n: answerers.length, right: M.right, total: M.cards.length, why: M.aborted || 'none' }) : '');
    voiceNote(voice, running() && ph !== 'join' ? 'all' : null, micOn());
    body.replaceChildren(); hostBox.replaceChildren();
    aform.style.display = ph === 'answer' && joined && !isSubject ? '' : 'none';
    qform.style.display = ph === 'ask' && isHost ? '' : 'none';
    alabel.textContent = t('mind.answer.label');
    qgo.textContent = t('mind.btn.ask');
    if (ph === 'join') {
      const rules = el('div', 'mrules'); rules.append(el('b', '', t('mind.rules.title')), el('p', '', t('mind.rules')));
      body.append(rules);
      const row = el('div', 'mrow');
      if (!joined) { const b = btn(t('mind.btn.join'), 'gprimary', () => act('join')); b.disabled = M.players.length >= MAX; row.append(b); }
      else if (!org) row.append(btn(t('mind.btn.unjoin'), '', () => act('unjoin')));
      if (org) {
        const opts = (role) => [[null, t('mind.pick.random')], ...M.players.filter((p) => p.id !== (role === 'subject' ? M.pick?.host : M.pick?.subject)).map((p) => [p.id, p.name])];
        const pick = el('div', 'mpick');
        pick.append(el('span', 'slabel', t('mind.pick.subject')), chips(opts('subject'), M.pick?.subject ?? null, (id) => act('pick', { role: 'subject', id })),
          el('span', 'slabel', t('mind.pick.host')), chips(opts('host'), M.pick?.host ?? null, (id) => act('pick', { role: 'host', id })));
        body.append(pick);
        const go = btn(t('mind.btn.go'), 'gprimary', () => act('go')); go.disabled = M.players.length < MIN; go.title = M.players.length < MIN ? t('mind.goTitle', { n: M.players.length, min: MIN }) : '';
        row.append(go, btn(t('mind.btn.cancel'), '', () => act('cancel')));
      } else if (joined && (!M.players.some((p) => p.id === M.organizer) || M.away.includes(M.organizer))) row.append(btn(t('mind.btn.go'), 'gprimary', () => act('go')));
      body.append(row);
    } else if (ph === 'ask' && isHost) {
      const tabs = chips(LEVELS.map((lv) => [lv, t(`mind.level.${lv}`)]), M.lv, (lv) => { M.lv = lv; M.page = 0; mRender(); });
      const list = M.questions.filter((q) => q.level === M.lv), pages = Math.max(1, Math.ceil(list.length / PER_PAGE)), page = M.page % pages;
      const qs = el('div', 'mqs');
      for (const q of list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)) { const b = btn(lang === 'en' ? q.en : q.zh, 'mqbtn', () => act('ask', { q: q.id })); b.type = 'button'; qs.append(b); }
      const more = btn(t('mind.btn.more'), '', () => { M.page = page + 1; mRender(); }); more.type = 'button';
      body.append(tabs, qs, more);
    } else if (ph === 'answer') {
      if (joined && !isSubject) {
        asend.textContent = t(M.mine ? 'mind.btn.resend' : 'mind.btn.send');
        anote.textContent = M.mine ? t('mind.answer.sent', { view: M.mine.view }) : '';
      }
      if (isHost) {
        hostBox.append(put(el('div', 'slabel'), 'shield', t('mind.host.only')));
        if (!M.hostAnswers.length) hostBox.append(el('div', 'gsys', t('mind.host.none')));
        for (const a of M.hostAnswers) { const r = el('div', 'mha'); r.append(el('b', '', a.name), ' ', el('span', '', a.view), ...(a.why ? [' ', el('span', 'mwhy', a.why)] : [])); hostBox.append(r); }
        const pub = btn(t('mind.btn.publish'), 'gprimary', () => act('publish')); pub.disabled = !M.hostAnswers.length;
        const row = el('div', 'mrow'); row.append(pub); hostBox.append(row);
      }
    } else if (ph === 'match') {
      const key = `${M.round}:${M.cards.length}`;
      if (M.guessKey !== key) { M.guessKey = key; M.guess = M.cards.map(() => ({ id: null, note: null })); }
      const list = el('div', 'mcards');
      M.cards.forEach((c, i) => list.append(card(c, i, (d) => {
        if (isSubject) {
          d.append(chips(M.candidates.map((p) => [p.id, p.name]), M.guess[i].id, (id) => {
            for (const g of M.guess) if (g.id === id) g.id = null;
            M.guess[i].id = M.guess[i].id === id ? null : id; mRender();
          }));
          const n = M.guess[i].note ??= field('input', 'mnoteinput', 'mind.match.note', 60); n.placeholder = t(n.dataset.ph); d.append(n);
        } else if (isHost) { const a = M.hostAnswers.find((x) => x.at === i) ?? M.hostAnswers.find((x) => x.at == null && x.view === c.view && (x.why ?? '') === c.why); if (a) d.append(el('div', 'mauthor', t('mind.card.author', { name: a.name }))); }
      })));
      body.append(list);
      if (!isSubject) body.append(el('div', 'mcand', t('mind.match.cands', { names: M.candidates.map((p) => p.name).join(t('mind.sep')) })));
      else {
        const n = M.guess.filter((g) => g.id).length;
        body.append(btn(t('mind.btn.confirm', { n, total: M.cards.length }), 'gprimary', () => act('match', { pairs: M.guess.map((g) => (g.id ? { id: g.id, note: g.note?.value ?? '' } : null)) })));
      }
    } else if (ph === 'reveal') {
      const list = el('div', 'mcards');
      M.cards.forEach((c, i) => list.append(card(c, i, (d) => {
        const g = M.guesses[i], ok = g?.id && g.id === M.authors[i];
        const r = el('div', 'mres ' + (ok ? 'ok' : 'no'));
        r.append(el('span', '', t('mind.res.by', { name: name(M.authors[i]) })), el('span', 'mguess', g?.id ? t('mind.res.guess', { name: name(g.id) }) : t('mind.res.none')), icon(ok ? 'check' : 'close'));
        d.append(r);
        if (g?.note) d.append(el('div', 'mwhy', t('mind.res.note', { name: name(M.subject), note: g.note })));
      })));
      body.append(list);
      if (joined) {
        // 结束 ends it for everyone: a second press to be sure
        const sure = endArm === M.round, end = btn(t(sure ? 'mind.btn.endSure' : 'mind.btn.end'), sure ? 'gdanger' : '', () => { if (endArm === M.round) act('end'); else { endArm = M.round; mRender(); } });
        const row = el('div', 'mrow'); row.append(btn(t('mind.btn.next'), 'gprimary', () => act('next')), end); body.append(row);
      }
    }
    if (running() && ph !== 'join' && !joined) {
      const b = btn(t('mind.btn.join'), '', () => act('join')); b.disabled = M.players.length >= MAX;
      body.append(el('div', 'gsys', t('mind.watch')), b);
    }
    body.style.display = body.childNodes.length ? '' : 'none';
    hostBox.style.display = hostBox.childNodes.length ? '' : 'none';
    // the players
    seats.replaceChildren();
    for (const p of M.players) {
      const s = el('div', 'seat' + (p.id === me ? ' me' : '') + (p.id === M.subject && ph !== 'join' ? ' speaking' : ''));
      const tag = M.away.includes(p.id) ? 'away' : ph === 'join' ? (p.id === M.organizer ? 'organizer' : 'joined') : p.id === M.subject ? 'subject' : p.id === M.host ? 'host'
        : ph === 'answer' && M.answered.includes(p.id) ? 'answered' : null;
      s.append(el('span', 'sav', [...p.name][0] ?? '?'), el('span', 'sname', p.id === me ? t('mind.you', { name: p.name }) : p.name), el('span', 'stag', tag ? t(TAG[tag]) : ''));
      seats.append(s);
    }
    seats.style.display = ph === 'final' ? 'none' : ''; // (the leaderboard instead)
    tipList(tips);
    tips.style.display = ph === 'join' || ph === 'ask' ? '' : 'none';
    scoreList(m.scores, M.scores, (s) => (M.away.includes(s.id) ? t('mind.away') : null));
    shellText();
    if (ph === 'final') {
      leaderboard(m.card, t('mind.board'), M.scores, me, [btn(t('mind.btn.again'), 'gprimary', () => send({ t: 'm_start' })), btn(t('mind.btn.leave'), '', full.close)]);
      for (const p of m.card.querySelectorAll('.gpts')) p.textContent = t('mind.points', { n: Number(p.textContent.replace(/\D+/g, '')) || 0 });
      m.card.insertBefore(el('div', 'gsys', t('mind.board.rule')), m.card.children[1]);
      m.card.style.display = '';
    } else m.card.style.display = 'none';
  }

  /** The round whose 结束 was pressed once (a second press ends the match). */
  let endArm = -1;
  /** What the log shows, as it came (codes): worded again when the language changes. */
  const logged = [];
  const log = (x, mine) => {
    logged.push([x, mine]); if (logged.length > 200) logged.shift();
    addLog(m.log, x.t === 'm_chat' ? { name: x.name, text: x.text } : x.t === 'm_err' ? { text: errText(x) } : line(x));
    if (mine) m.log.lastChild.classList.add('wpriv');
  };
  const errText = (x) => t(`mind.err.${['rate', 'phase', 'role', 'invalid', 'full', 'few', 'start'].includes(x.reason) ? x.reason : 'other'}`, { n: 0, min: MIN, ...(x.params ?? {}) });
  /** A refusal shown on the hut's card for a moment. */
  let lobNote = null;
  /** The shell's own words (games.js makes it): in this game's catalog keys, so they follow the language. */
  function shellText() {
    m.leave.textContent = t('mind.btn.leave'); m.leave.title = t('mind.leaveTitle'); m.sendBtn.textContent = t('mind.btn.chat');
    const head = m.scores.querySelector('.ghead'); if (head) head.textContent = t('mind.scores');
  }
  /** The hut's small card (no game on screen): start one, or go back to the one that's on. */
  function lobbyRender() {
    const here = inZone(MIND_HUT);
    lob.d.style.display = here && !M.open ? '' : 'none';
    if (!here || M.open) return;
    const n = hutCount(MIND_HUT), on = running();
    lob.count.textContent = t('mind.lobby.count', { n });
    lob.d.querySelector('.glrules').textContent = t('mind.lobby.rules');
    tipList(tipsLob);
    lob.see.textContent = t('mind.lobby.board');
    lob.see.style.display = !on && M.phase === 'final' ? '' : 'none';
    lob.see.onclick = open;
    if (on) {
      lob.status.textContent = M.phase === 'join' ? t('mind.lobby.onJoin') : t('mind.lobby.onRound', { n: M.round });
      lob.go.textContent = t(M.phase === 'join' && !M.players.some((p) => p.id === myId()) ? 'mind.lobby.look' : 'mind.lobby.back');
      lob.go.disabled = false; lob.go.title = '';
      lob.go.onclick = open;
    } else {
      lob.status.textContent = lobNote && lobNote.until > Date.now() ? lobNote.text : n >= MIN ? t('mind.lobby.ready') : t('mind.lobby.need', { min: MIN, more: MIN - n });
      lob.go.textContent = t(M.phase === 'final' ? 'mind.btn.again' : 'mind.lobby.start');
      lob.go.disabled = n < MIN; lob.go.title = n < MIN ? t('mind.lobby.need', { min: MIN, more: MIN - n }) : '';
      lob.go.onclick = () => send({ t: 'm_start' });
    }
  }
  /** Show or hide the full screen and the card (games.js calls it on every update and timer tick). */
  function render() {
    const here = inZone(MIND_HUT);
    if (!here) { M.open = false; M.dismissed = false; }
    m.root.style.display = here && M.open ? '' : 'none';
    if (here && M.open && running() && M.players.some((p) => p.id === myId())) autoTour('mind'); // players, not onlookers
    lobbyRender();
  }
  function open() { M.open = true; M.dismissed = false; mRender(); render(); }

  function line(x) {
    if (x.code) {
      const p = { ...(x.params ?? {}) };
      for (const [k, v] of Object.entries(p)) if (Array.isArray(v)) p[k] = v.join(t('mind.sep')); // (names: joined the language's way)
      return { text: t(`mind.msg.${x.code}`, p), good: x.good, icon: x.icon };
    }
    return { text: x.text ?? '', good: x.good, icon: x.icon }; // (an office that sends words)
  }
  function msg(x) {
    if (x.t === 'm_state') {
      const fresh = x.phase === 'join' && (M.phase === 'idle' || M.phase === 'final');
      if (fresh) { M.dismissed = false; m.log.replaceChildren(); logged.length = 0; endArm = -1; }
      if (x.round !== M.round || x.phase !== M.phase) { if (x.phase !== 'answer') M.mine = null; if (x.phase !== 'answer' && x.phase !== 'match') M.hostAnswers = []; }
      if (x.round !== M.round) { aview.value = ''; awhy.value = ''; qown.value = ''; M.page = 0; }
      if (x.phase === 'match' && M.phase !== 'match') M.guessKey = ''; // a new matching: no guesses from before
      Object.assign(M, x, { away: x.away ?? [] });
      if (x.phase !== 'idle' && !M.dismissed) M.open = true;
      if (x.phase === 'idle') M.open = false;
      mRender(); render();
    } else if (x.t === 'm_host') { M.hostAnswers = x.answers ?? []; if (x.questions) M.questions = x.questions; mRender(); }
    else if (x.t === 'm_mine') { M.mine = { view: x.view, why: x.why }; mRender(); }
    else if (x.t === 'm_msg') log(x, !!x.mine);
    else if (x.t === 'm_chat') log(x);
    else if (x.t === 'm_err') {
      const text = errText(x);
      log(x);
      lobNote = { text, until: Date.now() + 5000 }; lobbyRender(); // (a refused 开始: on the card)
    }
  }
  const secsLeft = (ts) => Math.max(0, Math.ceil((ts - Date.now()) / 1000));
  function tick() {
    const ts = running() && M.endsAt ? M.endsAt : 0;
    put(m.time, M.open && ts ? 'timer' : null, M.open && ts ? String(secsLeft(ts)) : '');
    m.time.classList.toggle('hurry', !!ts && secsLeft(ts) <= 10);
    if (M.open && running() && M.phase !== 'join') voiceNote(voice, 'all', micOn()); // (M pressed)
  }
  onLang(() => {
    put(m.root.querySelector('.gtitle'), 'heart', t('mind.title')); m.input.placeholder = t('mind.chat.placeholder');
    put(lob.d.querySelector('.glhead b'), 'heart', t('mind.title'));
    for (const f of [aview, awhy, qown]) f.placeholder = t(f.dataset.ph);
    const again = logged.splice(0); m.log.replaceChildren(); for (const [x, mine] of again) log(x, mine); // the lines in the new language
    mRender(); render();
  });
  mRender();
  return { M, root: m.root, msg, tick, render, running, open };
}
